import {generateId} from '../utils/id';
import {
    type CadEntityKind,
    isCadEntityKind,
} from './CadEntityTypes';

/**
 * CAD 实体 ID 固定前缀。
 *
 * 设计说明：
 * 1. 明确区分 CAD 持久实体 ID 与 Three.js `uuid`、Worker command id；
 * 2. 后续可以通过此前缀快速识别哪些 ID 已经进入 Phase 0 稳定 ID 体系。
 */
export const CAD_ENTITY_ID_PREFIX = 'cad';

/**
 * CAD 实体 ID 分隔符。
 */
const CAD_ENTITY_ID_SEPARATOR = ':';

/**
 * CAD 实体 ID 服务配置。
 */
export interface CadEntityIdServiceOptions {
    /** 当前文档 ID；存在时会写入生成的实体 ID。 */
    readonly documentId?: string;
    /** ID 工厂；测试中可注入确定性 ID。 */
    readonly idFactory?: () => string;
}

/**
 * CAD 实体 ID 创建输入。
 */
export interface CreateCadEntityIdInput {
    /** 实体类型。 */
    readonly kind: CadEntityKind;
    /** 可选本地 ID；未传入时使用 ID 工厂生成。 */
    readonly localId?: string;
    /** 可选文档 ID；优先级高于服务默认文档 ID。 */
    readonly documentId?: string;
}

/**
 * CAD 实体 ID 解析结果。
 */
export interface ParsedCadEntityId {
    /** 固定前缀，当前应始终为 `cad`。 */
    readonly prefix: typeof CAD_ENTITY_ID_PREFIX;
    /** 可选文档 ID。 */
    readonly documentId?: string;
    /** 实体类型。 */
    readonly kind: CadEntityKind;
    /** 本地实体 ID。 */
    readonly localId: string;
}

/**
 * CAD 实体 ID 服务。
 */
export interface CadEntityIdService {
    /**
     * 创建稳定 CAD 实体 ID。
     *
     * @param input 创建输入。
     * @returns 稳定 CAD 实体 ID。
     */
    readonly create: (input: CreateCadEntityIdInput) => string;
    /**
     * 解析稳定 CAD 实体 ID。
     *
     * @param entityId 待解析的实体 ID。
     * @returns 解析结果；非法 ID 返回 `null`。
     */
    readonly parse: (entityId: string) => ParsedCadEntityId | null;
    /**
     * 判断实体 ID 是否合法。
     *
     * @param entityId 待判断实体 ID。
     * @returns 是否为合法 CAD 实体 ID。
     */
    readonly isValid: (entityId: string) => boolean;
    /**
     * 断言实体 ID 合法。
     *
     * @param entityId 待断言实体 ID。
     * @returns 合法时返回解析结果。
     */
    readonly assertValid: (entityId: string) => ParsedCadEntityId;
}

/**
 * 校验 ID 片段。
 *
 * @param value 待校验片段。
 * @param label 错误消息中的片段名称。
 */
function assertIdSegment(value: string, label: string): void {
    if (!value.trim()) {
        throw new Error(`${label}不能为空。`);
    }

    if (value.includes(CAD_ENTITY_ID_SEPARATOR)) {
        throw new Error(`${label}不能包含分隔符 ${CAD_ENTITY_ID_SEPARATOR}。`);
    }
}

/**
 * 解析 CAD 实体 ID。
 *
 * @param entityId 待解析实体 ID。
 * @returns 解析成功返回结构化结果，否则返回 `null`。
 */
function parseCadEntityId(entityId: string): ParsedCadEntityId | null {
    const segments = entityId.split(CAD_ENTITY_ID_SEPARATOR);

    if (segments[0] !== CAD_ENTITY_ID_PREFIX) {
        return null;
    }

    if (segments.length === 3) {
        const [, kind, localId] = segments;

        if (!isCadEntityKind(kind) || !localId) {
            return null;
        }

        return {
            prefix: CAD_ENTITY_ID_PREFIX,
            kind,
            localId,
        };
    }

    if (segments.length === 4) {
        const [, documentId, kind, localId] = segments;

        if (!documentId || !isCadEntityKind(kind) || !localId) {
            return null;
        }

        return {
            prefix: CAD_ENTITY_ID_PREFIX,
            documentId,
            kind,
            localId,
        };
    }

    return null;
}

/**
 * 创建 CAD 实体 ID 服务。
 *
 * @param options 服务配置。
 * @returns CAD 实体 ID 服务。
 */
export function createCadEntityIdService(options: CadEntityIdServiceOptions = {}): CadEntityIdService {
    const idFactory = options.idFactory ?? generateId;

    return {
        create(input) {
            const localId = input.localId ?? idFactory();
            const documentId = input.documentId ?? options.documentId;

            assertIdSegment(localId, '实体本地 ID');

            if (documentId) {
                assertIdSegment(documentId, '文档 ID');

                return [
                    CAD_ENTITY_ID_PREFIX,
                    documentId,
                    input.kind,
                    localId,
                ].join(CAD_ENTITY_ID_SEPARATOR);
            }

            return [
                CAD_ENTITY_ID_PREFIX,
                input.kind,
                localId,
            ].join(CAD_ENTITY_ID_SEPARATOR);
        },
        parse: parseCadEntityId,
        isValid(entityId) {
            return parseCadEntityId(entityId) !== null;
        },
        assertValid(entityId) {
            const parsed = parseCadEntityId(entityId);

            if (!parsed) {
                throw new Error(`非法 CAD 实体 ID：${entityId}`);
            }

            return parsed;
        },
    };
}

